import { useRouter } from 'next/router';
import { createContext, useContext, useEffect, useReducer } from 'react';
import { filterValuesSeparator } from 'shared-helpers/consts';

import type { ParsedUrlQuery } from 'querystring';
import type { UserWithBasicData } from 'shared-types';

type Action = { type: 'updateQueryParams'; payload: ParsedUrlQuery };

const reducer = (state: MyTeamFollowContextType, action: Action) => {
  switch (action.type) {
    case 'updateQueryParams':
      const queryString = Object.keys(action.payload)
        .map((param) => {
          const value = action.payload[param];
          const queryValue = Array.isArray(value)
            ? value.join(filterValuesSeparator)
            : value;
          return `${param}=${encodeURIComponent(queryValue)}`;
        })
        .join('&');
      return { ...state, queryParams: action.payload, queryString };
    default:
      return state;
  }
};

type MyTeamFollowContextType = {
  queryParams: ParsedUrlQuery;
  queryString: string;
};

const MyTeamFollowContext = createContext<MyTeamFollowContextType>(undefined);

interface Props extends React.PropsWithChildren {}

const MyTeamFollowProvider = ({ children }: Props) => {
  const [state, dispatch] = useReducer(reducer, {
    queryParams: {},
    queryString: ''
  });

  const router = useRouter();

  useEffect(() => {
    if (!router.isReady) return;
    dispatch({ type: 'updateQueryParams', payload: router.query });
  }, [dispatch, router.isReady, router.query]);

  return (
    <MyTeamFollowContext.Provider value={state}>
      {children}
    </MyTeamFollowContext.Provider>
  );
};

const useMyTeamFollowContext = () => {
  const ctx = useContext(MyTeamFollowContext);
  return ctx;
};

/**
 * Filters and paginates a team detail view's user list from the URL-backed
 * filters (page/search/participantsOnly). The single-item responses already
 * carry every supervised user, so this is purely client-side — no refetch.
 *
 * Pass isParticipant to define who counts as a participant for the
 * participantsOnly filter; it differs per view (e.g. has progress vs added).
 */
const useTeamUserList = ({
  isParticipant,
  supervisedUsers
}: {
  isParticipant: (user: UserWithBasicData) => boolean;
  supervisedUsers: UserWithBasicData[];
}) => {
  const { queryParams } = useMyTeamFollowContext();

  const page = Number((queryParams['page'] || '1') as string);
  const participantsOnly = queryParams['participantsOnly'] === 'true';
  const search = queryParams['search'] as string;
  const usersPerPage = 10;

  const filteredUsers = supervisedUsers
    .filter((user) => !participantsOnly || isParticipant(user))
    .filter(
      (user) =>
        !search ||
        user.email?.includes(search) ||
        user.firstName?.includes(search) ||
        user.lastName?.includes(search) ||
        user.username?.includes(search)
    );

  const numberOfPages = Math.ceil(filteredUsers.length / usersPerPage);
  // clamp rather than reset the page: the filter controls live in the shared
  // MyTeamFollowBox, and resetting there would also reset the product list
  // view, where participantsOnly does not filter rows.
  const safePage = Math.min(page, numberOfPages || 1);

  const users = filteredUsers.slice(
    (safePage - 1) * usersPerPage,
    safePage * usersPerPage
  );

  return { numberOfPages, participantsOnly, search, users };
};

export { MyTeamFollowProvider, useMyTeamFollowContext, useTeamUserList };
